import { useLayoutEffect, useRef, useState } from 'react';
import { ButtonV2 } from '@cimplico/components';
import clsx from 'clsx';
import Editor from '../rich-text-editor/Editor';

export type MessageProps = {
    /**
     * Whether update query is loading
     */
    isSaving: boolean;
    /**
     * Whether content is currently being edited
     */
    isEditing: boolean;
    /**
     * Raw content of a comment/query/note
     */
    rawContent: string;
    /**
     * Structured content of a comment/query/note
     */
    structuredContent?: { [key: string]: unknown };
    /**
     * Callback when update action is cancelled
     */
    onCancel: () => void;
    /**
     * Callback when content is being updated
     */
    onUpdate: ({ rawContent, structuredContent }: { rawContent: string; structuredContent?: { [key: string]: unknown } }) => void;
};

/**
 * Middle content to display the message of a comment/query/note
 */
function Message({ isSaving, isEditing, rawContent, structuredContent, onCancel, onUpdate }: MessageProps): JSX.Element {
    const editorRef = useRef<HTMLDivElement>(null);
    const editorContainerRef = useRef<HTMLDivElement>(null);

    const [showingMore, setShowingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [editingText, setEditingText] = useState({ rawContent, structuredContent });

    useLayoutEffect(() => {
        if (!editorRef.current || !editorContainerRef.current) {
            setHasMore(false);
        } else {
            setHasMore(editorRef.current.clientHeight > editorContainerRef.current.clientHeight);
        }
    }, []);

    return (
        <div>
            <div
                className={clsx(
                    !isEditing && 'overflow-hidden',
                    isEditing || showingMore ? 'h-fit min-h-[150px]' : 'max-h-[150px]',
                    'flex flex-col'
                )}
                ref={editorContainerRef}
            >
                <div className="h-full grow basis-0 w-full" ref={editorRef}>
                    <Editor
                        defaultContent={structuredContent ? JSON.stringify(structuredContent) : undefined}
                        readOnly={!isEditing}
                        key="edit"
                        onContentChange={(raw, structured): void => {
                            setEditingText({ rawContent: raw, structuredContent: JSON.parse(structured) });
                        }}
                    />
                </div>
            </div>
            {/* Show more button for overflow */}
            {hasMore && !isEditing && (
                <button
                    className="ml-2 text-gray-800 font-medium text-xs"
                    onClick={(): void => {
                        setShowingMore(!showingMore);
                    }}
                >
                    {showingMore ? 'Show less' : 'Show more'}
                </button>
            )}
            {isEditing && (
                <div className="flex items-center gap-2 justify-end mt-1">
                    <ButtonV2 label="Cancel" size="xs" type="hollow" onClick={onCancel} isDisabled={isSaving} />
                    <ButtonV2
                        label="Save"
                        size="xs"
                        type="primary"
                        onClick={(): void => onUpdate(editingText)}
                        isDisabled={isSaving || !editingText.rawContent}
                        isLoading={isSaving}
                    />
                </div>
            )}
        </div>
    );
}

export default Message;
